// Contact.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./Contact.css";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: "", message: "" });

    // Validación básica
    if (!formData.name || !formData.email || !formData.message) {
      setStatus({
        type: "error",
        message: "Por favor, completa todos los campos obligatorios."
      });
      setLoading(false);
      return;
    }

    // Validación de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setStatus({
        type: "error",
        message: "Por favor, introduce un email válido."
      });
      setLoading(false);
      return;
    }

    try {
      // Llamada a tu API Laravel
      const apiBase =
        window.location.hostname === "localhost"
          ? "http://127.0.0.1:8000"
          : "https://api.wayoai.app";
      const response = await fetch(`${apiBase}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({
          type: "success",
          message: "¡Mensaje enviado correctamente! Te responderemos pronto."
        });
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        setStatus({
          type: "error",
          message: data.message || "Error al enviar el mensaje."
        });
      }
    } catch (error) {
      setStatus({
        type: "error",
        message: "Error de conexión. Intenta más tarde."
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contacto | Wayo - Tu Coach Nutricional</title>
        <meta name="description" content="Contacta con el equipo de Wayo. Envíanos tus preguntas, sugerencias o reporta un problema. Estamos aquí para ayudarte." />
        <meta name="keywords" content="contacto, soporte, ayuda, wayo, contactar" />
        <link rel="canonical" href="https://wayoai.app/contact" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Contacto | Wayo" />
        <meta property="og:description" content="Contacta con el equipo de Wayo. Estamos aquí para ayudarte." />
        <meta property="og:url" content="https://wayoai.app/contact" />
        <meta property="og:type" content="website" />
      </Helmet>

      <main className="contact-page">
        <Header />
      
      <section className="contact-content">
        <div className="contact-container">
          <div className="contact-header">
            <h1 className="contact-title">Contacto</h1>
            <p className="contact-subtitle">
              ¿Tienes alguna pregunta o sugerencia? Nos encantaría escucharte.
            </p>
          </div>

          <div className="contact-layout">
            {/* Formulario */}
            <div className="contact-form-section">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">
                    Nombre <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Tu nombre"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">
                    Asunto
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="">Selecciona un asunto</option>
                    <option value="general">Consulta General</option>
                    <option value="support">Soporte Técnico</option>
                    <option value="privacy">Privacidad y Datos</option>
                    <option value="feedback">Sugerencias</option>
                    <option value="business">Colaboraciones</option>
                    <option value="other">Otro</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">
                    Mensaje <span className="required">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Escribe tu mensaje aquí..."
                    rows="6"
                    disabled={loading}
                  />
                </div>

                {status.message && (
                  <div className={`status-message ${status.type}`}>
                    {status.type === "success" ? "✓ " : "⚠️ "}
                    {status.message}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? "Enviando..." : "Enviar Mensaje"}
                </button>
              </form>
            </div>

            {/* Información adicional */}
            <div className="contact-info-section">
              <div className="info-card">
                <div className="info-icon">📧</div>
                <h3>Email</h3>
                <a href="mailto:soporte@wayoai.app">soporte@wayoai.app</a>
                <p className="info-description">
                  Responderemos en un plazo máximo de 48 horas.
                </p>
              </div>

              <div className="info-card">
                <div className="info-icon">⏰</div>
                <h3>Tiempo de Respuesta</h3>
                <p>24-48 horas laborables</p>
                <p className="info-description">
                  De lunes a viernes, 9:00 - 18:00 CET
                </p>
              </div>

              <div className="info-card">
                <div className="info-icon">📍</div>
                <h3>Ubicación</h3>
                <p>Las Palmas de Gran Canaria</p>
                <p className="info-description">
                  España, Unión Europea
                </p>
              </div>

              <div className="info-card">
                <div className="info-icon">📄</div>
                <h3>Información Legal</h3>
                <div className="legal-links">
                  <Link to="/privacy">Política de Privacidad</Link>
                  <Link to="/terms">Términos y Condiciones</Link>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h2>Preguntas Frecuentes</h2>
            <div className="faq-grid">
              <div className="faq-item">
                <h4>¿Cómo puedo eliminar mi cuenta?</h4>
                <p>Ve a Mi Perfil → Eliminar mi cuenta</p>
              </div>
              <div className="faq-item">
                <h4>¿Necesito ser premium para usar la app?</h4>
                <p>No. La app tiene funcionalidades gratuitas y premium.</p>
              </div>
              <div className="faq-item">
                <h4>¿Cómo cancelo mi suscripción?</h4>
                <p>Gestiona tu suscripción desde tu App Store o Google Play.</p>
              </div>
              <div className="faq-item">
                <h4>¿Puedo escanear cualquier comida?</h4>
                <p>Sí. Puedes escanear cualquier comida para obtener información nutricional aproximada.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </main>
    </>
  );
}